import { toLocalYMD } from '../utils/hydration';
import { markOf, type Dose, type DoseMark, type IntakeItem } from '../utils/intakeSchedule';

/**
 * ⭐ "✓ All 4 taken": a GROUP of today's doses marked taken in one press — shared by Supplements
 * (`markGroupLinked`, which then keeps the habit in step) and Medicine (`medicationStore.markGroup`,
 * no habit). Supplements polish round, 2026-10-24.
 *
 *   · One dose after another, each WAITED for, through the store's own `markDose` (a change to the
 *     server's day, never a copy — lib/dayIdLog).
 *   · The record is READ AGAIN before each mark: a dose marked meanwhile (another tab, the widget)
 *     is left as it is — never a taken written over a skip. A dose no longer due (paused meanwhile)
 *     is passed over.
 *   · A refused mark does NOT stop the rest: each dose is its own write. What didn't save is named,
 *     honestly — "2 of 4 saved · Try again for Creatine · Omega-3" — and Try again = the SAME keys
 *     (what is now recorded is left as it is, so nothing is marked twice). "Saved" counts doses that
 *     now read TAKEN: one skipped elsewhere meanwhile is left out of both numbers (a skip is never
 *     taken).
 *   · ⚠️ One account: `stillHere` is asked before each mark and after the last; once it says no,
 *     nothing more is written (the next account never gets the last one's marks).
 *   · ⚠️ One day: past midnight mid-group (a bedtime "All 4 taken" at 23:59), it stops — the rest
 *     would land on the NEW day's record, ticking tomorrow's doses.
 *
 * Today only, for a record that HAS LOADED (`isInitialized`, `todayDate` = the calendar's today).
 */

export const ACCOUNT_CHANGED = 'The account changed meanwhile — nothing more was saved.';
export const DAY_CHANGED = 'The day changed meanwhile — nothing more was saved.';

/** What a group mark did, key by key (each key once, in the order asked). */
export interface GroupMarkResult {
  /** Every dose asked for is now recorded. */
  ok: boolean;
  /** Marked taken by this call — what its Undo would take back. */
  saved: string[];
  /** Already recorded when this call reached them (taken or skipped elsewhere) — left as they are. */
  already: string[];
  /** Didn't save. Try again with the same keys marks what is still open. */
  failed: string[];
  /** Words for what went wrong: "2 of 4 saved · Try again for Creatine · Omega-3". */
  error?: string;
}

/** The part of an intake store a group mark reads — its state as it is NOW. */
export interface DoseRecord {
  isInitialized: boolean;
  todayDate: string;
  todayTakenIds: string[];
  stack: readonly IntakeItem[];
  getTodayDoses: () => Dose[];
  markDose: (key: string, mark: DoseMark) => Promise<boolean>;
}

/** Names in a line, the first three and "+N": "Creatine · Omega-3 · Iron · +2". */
const namesLine = (names: readonly string[]): string =>
  names.length > 3 ? `${names.slice(0, 3).join(' · ')} · +${names.length - 3}` : names.join(' · ');

/**
 * The honest words for a group mark that didn't all save, or null when it did. `done` counts every
 * dose asked for that now reads TAKEN (this call's and those taken already) — never one skipped
 * elsewhere; `total` is those plus what didn't save. Names are each said once.
 *   groupMarkWords(2, 4, ['Creatine', 'Omega-3']) → "2 of 4 saved · Try again for Creatine · Omega-3"
 *   groupMarkWords(0, 2, ['Iron', 'Iron'])          → "Not saved · Try again for Iron"
 */
export function groupMarkWords(done: number, total: number, failedNames: readonly string[]): string | null {
  if (!failedNames.length || done >= total) return null;
  const names = [...new Set(failedNames)];
  return `${done > 0 ? `${done} of ${total} saved` : 'Not saved'} · Try again for ${namesLine(names)}`;
}

/** Mark each still-open dose of `keys` taken, one after another (see the header). */
export async function markDoseGroup(keys: readonly string[], o: {
  /** The store's state as it is NOW (`useXStore.getState`) — read again before each mark. */
  read: () => DoseRecord;
  /** False once the account has changed (signed out, another account in): stop there. */
  stillHere: () => boolean;
  /** What the store holds, for "Today’s supplements haven’t loaded yet". */
  noun: string;
}): Promise<GroupMarkResult> {
  const asked = [...new Set(keys)];
  const saved: string[] = [], already: string[] = [], failed: string[] = [];
  const answer = (error?: string): GroupMarkResult => ({ ok: !error && !failed.length, saved, already, failed, ...(error ? { error } : {}) });
  /** Nothing (more) is marked: the rest, from `i` on, didn't save. */
  const stop = (i: number, error: string) => { failed.push(...asked.slice(i)); return answer(error); };
  if (!o.stillHere()) return stop(0, ACCOUNT_CHANGED);
  const s0 = o.read();
  if (!s0.isInitialized || s0.todayDate !== toLocalYMD(new Date())) return stop(0, `Today’s ${o.noun} haven’t loaded yet — try again in a moment.`);
  const names: string[] = [];
  let takenAlready = 0;
  for (let i = 0; i < asked.length; i++) {
    const key = asked[i];
    if (!o.stillHere()) return stop(i, ACCOUNT_CHANGED);
    // Read again before each: one may have been marked (or paused) meanwhile.
    const s = o.read();
    // Past midnight: the store's day (or the calendar's) is no longer the one this press was for.
    if (s.todayDate !== s0.todayDate || toLocalYMD(new Date()) !== s0.todayDate) return stop(i, DAY_CHANGED);
    const dose = s.getTodayDoses().find(d => d.key === key);
    if (!dose) continue;
    const mark = markOf(s.todayTakenIds, key);
    if (mark !== null) { already.push(key); if (mark === 'taken') takenAlready++; continue; }
    let ok = false;
    try { ok = await s.markDose(key, 'taken'); } catch { ok = false; }
    if (ok) saved.push(key);
    else { failed.push(key); names.push(o.read().stack.find(it => it.id === dose.itemId)?.name ?? 'a dose'); }
  }
  // Signed out while the last one saved: nothing more is said to (or done for) the next account.
  if (!o.stillHere()) return answer(ACCOUNT_CHANGED);
  const done = saved.length + takenAlready;
  return answer(groupMarkWords(done, done + failed.length, names) ?? undefined);
}
